import React from "react";
import * as Icon from "react-feather";
import { Landmark } from "lucide-react";
import clsx from "clsx";

import WComponent from "../../../common/WComponent";
import UtilityService from "../../../../services/UtilityService";
import BookkeepingService from "../../../../services/BookkeepingService";

import ToolsModal from "../kit/ToolsModal";
import ToolsSummaryCard from "../kit/ToolsSummaryCard";

import "./manual-transfer-modal.css";

class ManualTransferModal extends WComponent {
	constructor(props) {
		super(props);

		const seed = props.seedTransactions || [];
		const outSeed = seed.find(transaction => Number(transaction.amount) < 0) || null;
		const inSeed = seed.find(transaction => Number(transaction.amount) > 0) || null;

		this.state = {
			outLeg: outSeed,
			inLeg: inSeed,
			activeSlot: !outSeed ? "out" : !inSeed ? "in" : null,
			search: "",
		};
	}

	chooseSlot = slot => this.update({ activeSlot: slot, search: "" });

	setLeg = transaction => {
		const { activeSlot } = this.state;
		if (activeSlot === "out") {
			this.update({ outLeg: transaction, activeSlot: this.state.inLeg ? null : "in", search: "" });
		} else {
			this.update({ inLeg: transaction, activeSlot: this.state.outLeg ? null : "out", search: "" });
		}
	};

	getCandidatesForActiveSlot = () => {
		const { transactions } = this.props;
		const { activeSlot, outLeg, inLeg, search } = this.state;
		if (!activeSlot) return [];

		const wantsMoneyOut = activeSlot === "out";
		const anchorLeg = wantsMoneyOut ? inLeg : outLeg;
		const trimmedSearch = search.trim().toLowerCase();

		const allTransactionsInDirection = (transactions || []).filter(transaction => (wantsMoneyOut ? Number(transaction.amount) < 0 : Number(transaction.amount) > 0));

		let candidates;
		if (trimmedSearch) {
			candidates = allTransactionsInDirection;
		} else if (anchorLeg) {
			const rankedCandidates = BookkeepingService.getManualTransferCandidates({ transactions, leg: anchorLeg });
			const hasRankedMatch = rankedCandidates.length > 0;
			candidates = hasRankedMatch ? rankedCandidates : allTransactionsInDirection;
		} else {
			candidates = allTransactionsInDirection;
		}

		const withoutChosenLegs = candidates.filter(transaction => transaction !== outLeg && transaction !== inLeg);
		if (!trimmedSearch) return withoutChosenLegs.slice(0, 50);

		const matching = withoutChosenLegs.filter(transaction => {
			const accountName = transaction.bankAccountName || transaction.accountName || "";
			const haystack = `${accountName} ${transaction.description || ""} ${transaction.amount}`.toLowerCase();
			return haystack.includes(trimmedSearch);
		});
		return matching.slice(0, 50);
	};

	getValidation = () => {
		const { outLeg, inLeg } = this.state;
		if (!outLeg || !inLeg) return null;
		return BookkeepingService.getManualTransferValidation({ transactionA: outLeg, transactionB: inLeg });
	};

	getAmountParts = transaction => {
		const currencyCode = transaction.currency || "CAD";
		const isForeign = currencyCode !== "CAD";
		const nativeAmount = isForeign && transaction.originalAmount != null ? Number(transaction.originalAmount) : Number(transaction.amount);
		const nativeText = UtilityService.formatCurrency(nativeAmount).replace("$", "");
		const cadText = isForeign ? `≈ CAD ${UtilityService.formatCurrency(Number(transaction.amount) || 0)}` : "";
		const isNegative = Number(transaction.amount) < 0;
		return { currencyCode, isForeign, nativeText, cadText, isNegative };
	};

	onConfirm = () => {
		const validation = this.getValidation();
		if (!validation || !validation.isValid) return;
		const { outLeg, inLeg } = this.state;
		this.props.onConfirm({ transactionA: outLeg, transactionB: inLeg });
	};

	renderLegSlot = slot => {
		const { activeSlot } = this.state;
		const leg = slot === "out" ? this.state.outLeg : this.state.inLeg;
		const label = slot === "out" ? "From — money out" : "To — money in";
		const isActive = activeSlot === slot;

		if (!leg) {
			return (
				<button type="button" className={clsx("mt-leg__empty", isActive && "mt-leg__empty--active")} onClick={() => this.chooseSlot(slot)}>
					<span className="mt-leg__empty-label">{label}</span>
					<span className="mt-leg__empty-hint">
						<Icon.Plus size={14} />
						Pick a transaction below
					</span>
				</button>
			);
		}

		const accountName = leg.accountName || leg.bankAccountName || "";
		const primaryText = accountName || leg.description;
		const tone = slot === "out" ? "danger" : "success";
		const amountParts = this.getAmountParts(leg);

		const meta = [{ icon: <Icon.Calendar size={13} />, text: leg.date }];
		if (accountName && leg.description) meta.push({ icon: <Icon.FileText size={13} />, text: leg.description });

		return (
			<div className="mt-leg">
				<div className="mt-leg__head">
					<span className="mt-leg__label">{label}</span>
					<button type="button" className="tk-text-action" onClick={() => this.chooseSlot(slot)}>Change</button>
				</div>
				<ToolsSummaryCard
					icon={<Landmark size={20} />}
					tone={tone}
					title={primaryText}
					value={amountParts.nativeText}
					valueSuffix={amountParts.currencyCode}
					meta={meta}
				/>
				{amountParts.isForeign && <div className="mt-leg__cad">{amountParts.cadText}</div>}
			</div>
		);
	};

	renderCandidateRow = transaction => {
		const accountName = transaction.accountName || transaction.bankAccountName || "";
		const anchorLeg = this.state.activeSlot === "out" ? this.state.inLeg : this.state.outLeg;
		const isMatch = !!anchorLeg && BookkeepingService.getManualTransferValidation({ transactionA: anchorLeg, transactionB: transaction }).isValid;
		const amountParts = this.getAmountParts(transaction);
		const amountClass = "tk-amount " + (amountParts.isNegative ? "tk-amount--out" : "tk-amount--in");

		return (
			<button type="button" key={`${transaction.date}:${transaction.amount}:${accountName}:${transaction.description}`} className={clsx("mt-candidate", isMatch && "mt-candidate--match")} onClick={() => this.setLeg(transaction)}>
				<span className="tk-icon-chip mt-candidate__chip">
					<Landmark size={14} />
				</span>
				<div className="mt-candidate__main">
					<div className="mt-candidate__top">
						<span className="mt-candidate__name">{accountName || transaction.description}</span>
						{isMatch && <span className="tk-badge tk-badge--success">Match</span>}
					</div>
					<div className="mt-candidate__detail">{transaction.date} · {transaction.description}</div>
				</div>
				<div className="mt-candidate__amount">
					<span className={amountClass}>{amountParts.nativeText} {amountParts.currencyCode}</span>
					{amountParts.isForeign && <span className="mt-candidate__cad">{amountParts.cadText}</span>}
				</div>
			</button>
		);
	};

	renderPicker = () => {
		const { activeSlot, search } = this.state;
		if (!activeSlot) return null;

		const candidates = this.getCandidatesForActiveSlot();
		const heading = activeSlot === "out" ? "Choose the money-out transaction" : "Choose the money-in transaction";

		return (
			<div className="mt-picker">
				<div className="mt-picker__heading">{heading}</div>
				<div className="tk-search">
					<Icon.Search size={15} className="tk-search__icon" />
					<input
						className="tk-search__input"
						placeholder="Search transactions…"
						value={search}
						onChange={event => this.update({ search: event.target.value })}
					/>
				</div>
				<div className="mt-candidate-list">
					{candidates.length === 0 ? (
						<div className="mt-candidate-empty">No matching transactions.</div>
					) : (
						candidates.map(this.renderCandidateRow)
					)}
				</div>
			</div>
		);
	};

	renderStatus = () => {
		const validation = this.getValidation();
		if (!validation) return null;

		if (!validation.isValid) {
			return (
				<div className="mt-status mt-status--error">
					<Icon.AlertTriangle size={15} />
					<span>{validation.error}</span>
				</div>
			);
		}

		const effect = validation.isCrossCurrency
			? `These accounts are in different currencies. Recording that needs the CAD ${validation.differenceCad.toFixed(2)} exchange difference recognised as a foreign exchange gain or loss, which is not built yet — record it by hand for now.`
			: "Ready. The payment is recorded once, out of one account and into the other, and the second line stops posting so it is not counted twice.";
		return (
			<div className="mt-status mt-status--ok">
				<Icon.CheckCircle size={15} />
				<span>{effect}</span>
			</div>
		);
	};

	renderFooter = () => {
		const validation = this.getValidation();
		const canConfirm = !!(validation && validation.isValid);

		return (
			<>
				<span className="mt-footer__hint">Both sides must be opposite amounts within tolerance.</span>
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={this.props.onClose}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" onClick={this.onConfirm} disabled={!canConfirm}>
						<Icon.Repeat size={16} />
						Create transfer
					</button>
				</span>
			</>
		);
	};

	render() {
		return (
			<ToolsModal open={true} onClose={this.props.onClose} title="Mark as transfer" width={640} footer={this.renderFooter()}>
				<p className="mt-intro">Pair the two sides of a transfer the tool didn't match automatically — the transaction that left one account and the one that arrived in another.</p>

				<div className="mt-legs">
					{this.renderLegSlot("out")}
					<span className="mt-legs__connector"><Icon.ArrowDown size={18} /></span>
					{this.renderLegSlot("in")}
				</div>

				{this.renderStatus()}
				{this.renderPicker()}
			</ToolsModal>
		);
	}
}

export default ManualTransferModal;
